import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import { Button } from "../components/ui";
import SignOff from "../components/SignOff";
import CleaningReferenceModal from "../components/CleaningReference";
import ReportActions from "../components/ReportActions";
import { useAuth } from "../auth";
import { apiGet, apiSend } from "../api";
import { getDeviceId } from "../deviceId";
import { getSticky } from "../sticky";
import { formatDateTime, formatStamp } from "../dateutil";
import { cleaningFreqLabel, SHIFT_FREQUENCIES } from "../constants";
import { C, R, S } from "../theme";

const CYAN = "#06b6d4";

export default function CleaningChecklist() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [unit, setUnit] = useState(null);
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("shift"); // shift | all
  const [st, setStt] = useState({}); // itemId -> {done, note, photo}
  const [notes, setNotes] = useState("");
  const [signoff, setSignoff] = useState({ signature: null, name: user?.display_name || user?.company_name || "", initials: "", complete: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [refOpen, setRefOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const submitting = useRef(false);
  const loc = getSticky("location");

  useEffect(() => {
    apiGet(`/attractions/${id}`).then(setUnit).catch(() => {});
    apiGet(`/attractions/${id}/cleaning-schedule`).then(setItems).catch(() => {});
  }, [id]);

  const visible = scope === "shift" ? items.filter((it) => SHIFT_FREQUENCIES.includes(it.frequency)) : items;
  const stOf = (iid) => st[iid] || { done: false, note: "", photo: null };
  const setItem = (iid, patch) => setStt((p) => ({ ...p, [iid]: { ...stOf(iid), ...patch } }));
  const doneCount = visible.filter((it) => stOf(it.id).done).length;

  const addPhoto = (iid) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = () => { const f = inp.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => setItem(iid, { photo: r.result }); r.readAsDataURL(f); };
    inp.click();
  };

  const submit = async () => {
    if (submitting.current) return;
    setErr("");
    if (!visible.length) { setErr("There are no cleaning tasks to complete."); return; }
    if (!signoff.complete || !signoff.signature) { setErr("Add your name and signature to sign off the cleaning."); return; }
    submitting.current = true; setBusy(true);
    try {
      const results = visible.map((it) => {
        const s = stOf(it.id);
        return { item_id: it.id, label: it.name, frequency: it.frequency, type: "pass_fail", value: s.done ? "pass" : "fail", status: s.done ? "pass" : "fail", note: s.note || null, photo: s.photo || null };
      });
      const overall = results.some((r) => r.status === "fail") ? "fail" : "pass";
      const body = { attraction_id: id, log_type: "cleaning", results, signature: signoff.signature, signed_by_name: signoff.name, signed_by_initials: signoff.initials, device_id: getDeviceId(), overall_result: overall, location: loc || null, general_notes: notes || null, photos: [] };
      const log = await apiSend("/logs", "POST", body);
      setSummary(log);
    } catch (e) { setErr(e?.message || "Could not save the cleaning log"); }
    finally { submitting.current = false; setBusy(false); }
  };

  if (summary) {
    const failed = summary.overall_result === "fail";
    return (
      <div className="fade-up" style={{ padding: S.lg }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: S.sm, background: failed ? C.red : C.green, borderRadius: R.md, padding: `${S.md}px 0`, marginBottom: S.md }}>
          <Icon name={failed ? "warning" : "checkmark-circle"} size={26} color={failed ? C.text : C.black} /><span className="display" style={{ fontSize: 20, fontWeight: 900, color: failed ? C.text : C.black }}>{failed ? "LOGGED — TASKS OUTSTANDING" : "CLEANING COMPLETE"}</span>
        </div>
        <div style={{ color: C.text, fontSize: 18, fontWeight: 900 }}>{summary.attraction_name}</div>
        <div style={{ color: C.muted, fontSize: 13, marginBottom: S.md }}>Cleaning & Sanitising · {formatDateTime(summary.submitted_at)}</div>
        <ReportActions report={{
          title: `${summary.attraction_name} — Cleaning Checklist`,
          company: summary.signed_by_name,
          rows: summary.results || [],
          columns: [{ label: "Task", get: (r) => r.label }, { label: "Frequency", get: (r) => cleaningFreqLabel(r.frequency) }, { label: "Status", get: (r) => (r.status === "fail" || r.value === "fail" ? "NOT DONE" : "COMPLETED") }, { label: "Note", get: (r) => r.note || "-" }],
          photos: (summary.results || []).filter((r) => r.photo).map((r) => ({ label: r.label, dataUri: r.photo })),
          meta: { details: [{ label: "Unit", value: summary.attraction_name }, { label: "Check", value: "Cleaning & Sanitising" }, { label: "Result", value: (summary.overall_result || "pass").toUpperCase() }, { label: "Filed On", value: formatDateTime(summary.submitted_at) }, { label: "Operator", value: summary.signed_by_name }], notes: summary.general_notes, signature: summary.signature, signedBy: summary.signed_by_name, initials: summary.signed_by_initials, submittedAt: formatStamp(summary.submitted_at), docType: "CL", recordId: summary.id, siteLocation: summary.location, attractionName: summary.attraction_name },
        }} />
        <button data-testid="cleaning-done" className="pressable" onClick={() => nav(`/log/${summary.id}`)} style={{ width: "100%", background: C.amber, color: C.black, border: "none", borderRadius: R.pill, minHeight: 52, fontWeight: 900, fontSize: 16, marginTop: S.md }}>View Cleaning Log</button>
        <button className="pressable" onClick={() => nav(`/attraction/${id}`)} style={{ width: "100%", background: "transparent", color: C.muted, border: "none", minHeight: 44, fontWeight: 700, marginTop: S.xs }}>Back to unit</button>
      </div>
    );
  }

  return (
    <div data-testid="cleaning-checklist-page" className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button data-testid="cc-back" className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div style={{ flex: 1 }}>
          <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Cleaning Checklist</div>
          {unit && <div style={{ color: C.muted, fontSize: 13 }}>{unit.name}</div>}
        </div>
        <button data-testid="cc-reference" className="pressable" onClick={() => setRefOpen(true)} style={{ width: 40, height: 40, borderRadius: 11, background: CYAN, display: "flex", alignItems: "center", justifyContent: "center", border: "none" }}><Icon name="information-circle" size={22} color={C.black} /></button>
      </div>

      <div style={{ padding: `0 ${S.lg}px ${S.xxl}px` }}>
        <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
          {[["shift", "This shift"], ["all", "All tasks"]].map(([k, lbl]) => (
            <button key={k} data-testid={`cc-scope-${k}`} className="pressable" onClick={() => setScope(k)} style={{ flex: 1, minHeight: 44, borderRadius: R.pill, border: `1px solid ${scope === k ? CYAN : C.borderStrong}`, background: scope === k ? CYAN : C.card2, color: scope === k ? C.black : C.sub, fontWeight: 800, fontSize: 14 }}>{lbl}</button>
          ))}
        </div>
        <div style={{ color: C.muted, fontSize: 13, marginBottom: S.md }}>{doneCount}/{visible.length} tasks completed{scope === "shift" ? " · showing Every Shift + Daily tasks" : ""}</div>

        {visible.length === 0 ? (
          <div style={{ textAlign: "center", padding: `${S.xl}px 0`, color: C.muted }}><Icon name="sparkles" size={44} color={C.muted} /><div style={{ marginTop: S.sm }}>No cleaning tasks {scope === "shift" ? "due this shift" : "configured"}</div></div>
        ) : visible.map((it) => {
          const s = stOf(it.id);
          return (
            <div key={it.id} data-testid={`cc-item-${it.id}`} style={{ background: C.card, borderRadius: R.md, border: `1px solid ${s.done ? C.green : C.border}`, padding: S.md, marginBottom: S.sm }}>
              <div style={{ display: "flex", alignItems: "center", gap: S.md }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontWeight: 800, fontSize: 16 }}>{it.name}</div>
                  <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>{cleaningFreqLabel(it.frequency)}{it.precautions ? ` · ${it.precautions}` : ""}</div>
                </div>
                <button data-testid={`cc-complete-${it.id}`} className="pressable" onClick={() => setItem(it.id, { done: !s.done })} style={{ display: "flex", alignItems: "center", gap: 6, background: s.done ? C.green : C.card2, color: s.done ? C.black : C.amber, border: `1px solid ${s.done ? C.green : C.borderStrong}`, borderRadius: R.pill, padding: "8px 14px", minHeight: 44, fontWeight: 900, fontSize: 13, flexShrink: 0 }}>
                  <Icon name={s.done ? "checkmark-circle" : "checkmark-done"} size={18} color={s.done ? C.black : C.amber} /> {s.done ? "COMPLETED" : "MARK DONE"}
                </button>
              </div>
              {!!it.method && <div style={{ color: C.sub, fontSize: 12.5, marginTop: S.sm, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>{it.method}</div>}
              <div style={{ display: "flex", gap: S.sm, marginTop: S.sm }}>
                <input data-testid={`cc-note-${it.id}`} value={s.note} onChange={(e) => setItem(it.id, { note: e.target.value })} placeholder="Note (if extra attention needed)" style={{ flex: 1, background: C.card2, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 14, minHeight: 44, padding: `0 ${S.md}px`, outline: "none", boxSizing: "border-box" }} />
                <button data-testid={`cc-photo-${it.id}`} className="pressable" onClick={() => addPhoto(it.id)} style={{ background: s.photo ? "rgba(16,185,129,0.15)" : C.card2, border: `1px solid ${s.photo ? C.green : C.borderStrong}`, borderRadius: R.md, minHeight: 44, padding: `0 ${S.md}px`, color: s.photo ? C.green : C.amber, fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}><Icon name="camera" size={18} color={s.photo ? C.green : C.amber} />{s.photo ? "✓" : ""}</button>
              </div>
              {s.photo && <img src={s.photo} alt="" style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: R.sm, marginTop: S.sm }} />}
            </div>
          );
        })}

        {visible.length > 0 && (
          <>
            <div style={{ color: C.sub, fontSize: 13, fontWeight: 800, textTransform: "uppercase", margin: `${S.lg}px 0 ${S.sm}px` }}>General notes (optional)</div>
            <textarea data-testid="cc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else worth recording…" style={{ width: "100%", background: C.card2, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 15, minHeight: 70, padding: S.md, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            <div style={{ height: S.md }} />
            <SignOff defaultName={signoff.name} onChange={setSignoff} />
            {!!err && <div data-testid="cc-error" style={{ color: C.red, fontWeight: 800, fontSize: 14, marginTop: S.sm }}>{err}</div>}
            <div style={{ height: S.sm }} />
            <Button title={busy ? "Saving…" : "Submit Cleaning Log"} testID="cc-submit" onPress={submit} disabled={busy} />
          </>
        )}
      </div>
      <CleaningReferenceModal open={refOpen} onClose={() => setRefOpen(false)} />
    </div>
  );
}
